import {AsyncCollectionConcurrentFetchingAction} from '../async-collection/async-collection';
import {AsyncListBase} from '../async-list-base/async-list-base';
import {AsyncListFetchResult} from "../async-list/async-list";
import {Deferred} from "../async-utils/async-utils";
import {cachedValueCreate} from "../cached-value/cached-value";

const NoCursorSymbol: unique symbol = Symbol('NoCursorSymbol');
type NoCursorSymbol = typeof NoCursorSymbol;

export namespace AsyncListWithPaginationCursor {
    export interface FetchResult<T, CURSOR> extends AsyncListFetchResult<T> {
        cursor: CURSOR | null;
        totalCount?: number;
    }

    export interface AsyncListStructure<T, CURSOR> {
        items: T[];
        cursor: CURSOR | NoCursorSymbol | null;
        totalCount: number | undefined;
    }

    export type FetchContext<CURSOR> = {
        pageSize: number | undefined;
    } & (
        {isInitialFetch: true} |
        {isInitialFetch: false; cursor: CURSOR}
    )

    /**
     * Options for AsyncListWithPaginationCursor.
     * Extends AsyncListBase.Options - getItemId is inherited from there.
     */
    export interface AsyncListOptions<T, CURSOR> extends Omit<AsyncListBase.Options<T, AsyncListStructure<T, CURSOR>>, 'initialValue' | 'fetchFunction' | 'concurrentFetchingAction'> {
        fetchFunction: (fetchContext: FetchContext<CURSOR>) => Promise<FetchResult<T, CURSOR>>;
    }
}

export class AsyncListWithPaginationCursor<T, CURSOR>
    extends AsyncListBase<T, AsyncListWithPaginationCursor.AsyncListStructure<T, CURSOR>> {

    private readonly _fetchFunction: AsyncListWithPaginationCursor.AsyncListOptions<T, CURSOR>['fetchFunction'];

    constructor(options: AsyncListWithPaginationCursor.AsyncListOptions<T, CURSOR>) {

        const {
            fetchFunction,
            getItemId,
        } = options;

        super({
            getItemId,
            initialValue: {
                items: [],
                cursor: NoCursorSymbol,
                totalCount: undefined
            },
            concurrentFetchingAction: (param) => {
                if (param === undefined) {
                    return AsyncCollectionConcurrentFetchingAction.skipAndWaitForPendingRequestCompletion();
                } else {
                    return AsyncCollectionConcurrentFetchingAction.serializeAfterPendingRequestCompletion();
                }
            },
            fetchFunction: async (context) => {

                const prevValue = context.currentValue;

                if (prevValue.cursor === null) {
                    return context.skip();
                }

                const isInitialFetch = prevValue.cursor === NoCursorSymbol;

                const fetchResult = await fetchFunction(prevValue.cursor === NoCursorSymbol ?
                    {
                        pageSize: undefined,
                        isInitialFetch: true
                    } :
                    {
                        pageSize: undefined,
                        isInitialFetch: false,
                        cursor: prevValue.cursor
                    });

                return {
                    items: [...prevValue.items, ...fetchResult.results],
                    cursor: fetchResult.cursor,
                    totalCount: isInitialFetch ? fetchResult.totalCount : prevValue.totalCount
                }
            }
        });

        this._fetchFunction = fetchFunction;
    }

    getFetchedItemsCount () {
        return this.getItems().length;
    }

    getTotalCount () {
        return super.getValue().totalCount;
    }

    getItems () {
        return super.getValue().items;
    }

    hasMore () {
        return super.getValue().cursor !== null;
    }

    async fetchAllItems (
        options: {
            limit?: number;
        } = {}
    ) {
        const {
            limit = Infinity
        } = options;

        while (this.hasMore() && this.getFetchedItemsCount() < limit) {
            await this.triggerFetch();
        }

        return this.getItems();
    }

    public supportsRefresh(): boolean {
        return true;
    }

    /**
     * Refreshes the list by re-fetching items from the beginning without showing a loading state.
     * 
     * - If no items are loaded, performs a single initial fetch
     * - If items are loaded, iterates through pages until reaching the same item count
     * - Deduplication is handled automatically by processValueAfterFetch() when getItemId is provided
     * - Items remain visible during the refresh (no intermediate empty state)
     * - If multiple refreshes are triggered, only the last one updates the state
     * - Errors are swallowed to keep the existing data visible
     * @param refreshOperation The refresh operation promise to check if still current
     */
    protected async refreshInternal(refreshOperation: Promise<void>): Promise<void> {
        const targetItemCount = this.getFetchedItemsCount();
        
        try {
            // Start with initial fetch
            let result = await this._fetchFunction({
                pageSize: undefined,
                isInitialFetch: true
            });
            
            let allItems = [...result.results];
            let currentCursor: CURSOR | null = result.cursor;
            const totalCount = result.totalCount;
            
            // If we had items before, continue fetching until we reach the same count
            while (targetItemCount > 0 && currentCursor !== null && allItems.length < targetItemCount) {
                result = await this._fetchFunction({
                    pageSize: undefined,
                    isInitialFetch: false,
                    cursor: currentCursor
                });
                
                allItems = [...allItems, ...result.results];
                currentCursor = result.cursor;
            }
            
            // Deduplication is handled automatically by processValueAfterFetch() via handleUpdateFromRefresh()
            this.handleUpdateFromRefresh(refreshOperation, {
                items: allItems,
                cursor: currentCursor,
                totalCount: totalCount
            });
        } catch {
            // Swallow errors to keep existing data visible
        }
    }
}

export function asyncListWithPaginationCursorCreate<T, CURSOR> (options: AsyncListWithPaginationCursor.AsyncListOptions<T, CURSOR>) {
    return new AsyncListWithPaginationCursor<T, CURSOR>(options);
}

const lazyAsyncListWithPaginationCursorUnknown = cachedValueCreate(() => {
    const infiniteDeferred = new Deferred<AsyncListWithPaginationCursor.FetchResult<any, any>>();

    return new AsyncListWithPaginationCursor({
        fetchFunction: async () => {
            return infiniteDeferred.promise;
        }
    })
})


export function asyncListWithPaginationCursorCreateUnknown<T> () : AsyncListWithPaginationCursor<T, unknown> {
    return lazyAsyncListWithPaginationCursorUnknown.getValue();
}